/* eslint-disable @typescript-eslint/no-explicit-any */

declare const abp: any;

/**
 * The one thing that outlives a session ending: it is what marks this browser as trusted, and
 * an expired session must not turn into a fresh two-factor challenge.
 */
export const TWO_FACTOR_TRUST_TOKEN_KEY = 'twoFactorTrustToken';

/** Why the login screen is being shown, carried on the URL because the redirect is a full load. */
export type SessionEndReason = 'expired' | 'unauthorized';

export const SESSION_END_REASON_PARAM = 'reason';
export const RETURN_PATH_PARAM = 'from';

const SESSION_END_MESSAGES: Record<SessionEndReason, string> = {
  expired: 'Your session has expired. Please sign in again.',
  unauthorized: 'Your session is no longer valid. Please sign in again.',
};

export const sessionEndMessage = (reason: string | null): string | undefined =>
  reason ? SESSION_END_MESSAGES[reason as SessionEndReason] : undefined;

/**
 * A return path is only followed when it is a path on this site, or the login screen becomes an
 * open redirect. `//host` is the obvious way in; `/\host` is the same trick, since a browser
 * reads the backslash as the second slash.
 */
export const safeReturnPath = (value: string | null): string | undefined => {
  if (!value || !value.startsWith('/')) return undefined;

  const second = value.charAt(1);
  return second === '/' || second === '\\' ? undefined : value;
};

/**
 * Set the moment the browser's copy of the session goes, however it went. It stops a second
 * caller from redirecting on top of the first, keeps requests that fail in its wake from each
 * announcing the same thing, and keeps the expiry watch from mistaking a deliberate sign-out
 * for a session that ran out.
 */
let sessionOver = false;

/** True once the session is over and something is already dealing with it. */
export const isSessionEnding = () => sessionOver;

/**
 * Drops everything the browser holds about the signed-in user, keeping the trust token above.
 * Storage can throw outright (private windows, blocked site data), so the token - the part that
 * actually ends the session - is cleared either way.
 */
export const clearStoredSession = () => {
  sessionOver = true;

  try {
    const trustToken = localStorage.getItem(TWO_FACTOR_TRUST_TOKEN_KEY);
    localStorage.clear();
    sessionStorage.clear();
    if (trustToken) {
      localStorage.setItem(TWO_FACTOR_TRUST_TOKEN_KEY, trustToken);
    }
  } catch {
    // storage unavailable - nothing to clear
  }

  abp.auth.clearToken();
};

/**
 * Ends the session the user did not choose to end: clears the browser's copy of it and sends
 * them to the login screen, saying why and remembering where they were. A sign-out goes through
 * clearStoredSession instead, and does its own navigating.
 */
export const endSession = (reason: SessionEndReason = 'expired') => {
  if (sessionOver) return;

  const { pathname, search } = window.location;

  clearStoredSession();

  const params = new URLSearchParams({ [SESSION_END_REASON_PARAM]: reason });
  if (pathname !== '/' && !pathname.startsWith('/login')) {
    params.set(RETURN_PATH_PARAM, `${pathname}${search}`);
  }

  // A full load rather than a router navigation: it is the only way to be sure no store, timer
  // or socket belonging to the ended session survives into the next one.
  window.location.href = `/login?${params.toString()}`;
};
